import React from 'react'

export type StatusChipColor =
  | 'green'
  | 'darkGreen'
  | 'yellow'
  | 'blue'
  | 'gray'
  | 'red'
  | 'orange'

export interface StatusChipProps {
  color: StatusChipColor
  label: string
  className?: string
}

// Inline style colors (Tailwind palette approximations)
// - Greens darkened, text uses a contrasting shade of the same hue
const colorMap: Record<
  StatusChipColor,
  { backgroundColor: string; textColor: string }
> = {
  // Active
  green: { backgroundColor: '#16a34a', textColor: '#dcfce7' }, // bg: green-600, text: green-100
  // Approved (now dark green)
  darkGreen: { backgroundColor: '#166534', textColor: '#bbf7d0' }, // bg: green-800, text: green-200
  // Archived
  yellow: { backgroundColor: '#facc15', textColor: '#713f12' }, // bg: yellow-400, text: yellow-900 (amber-ish)
  // Completed
  blue: { backgroundColor: '#3b82f6', textColor: '#dbeafe' }, // bg: blue-500, text: blue-100
  // Paused
  gray: { backgroundColor: '#9ca3af', textColor: '#1f2937' }, // bg: gray-400, text: gray-800
  // Rejected
  red: { backgroundColor: '#ef4444', textColor: '#fee2e2' }, // bg: red-500, text: red-100
  // Orange option
  orange: { backgroundColor: '#f97316', textColor: '#ffedd5' }, // bg: orange-500, text: orange-100
}

export const StatusChip: React.FC<StatusChipProps> = ({
  color,
  label,
  className,
}) => {
  const { backgroundColor, textColor } = colorMap[color] ?? colorMap.gray

  return (
    <span
      style={{ backgroundColor, color: textColor }}
      className={`font-small inline-flex items-center justify-center whitespace-nowrap rounded-full px-2 py-0.5 text-center text-xs ${
        className ? className : ''
      }`.trim()}
      aria-label={`Status: ${label}`}
    >
      {label}
    </span>
  )
}
